import React from 'react'
import PropTypes from 'prop-types'
import { getContrastRatio, makeStyles } from '@material-ui/core'
import clsx from 'clsx'
import { cssVar } from '../../../utils/cssVar'

const cardVariants = {
  default: () => ({
    border: 'var(--tc-card-border)',
    borderRadius: 'var(--tc-card-border-radius)',
    backgroundColor: 'var(--tc-card-bg-color)',
    boxShadow: 'var(--tc-card-shadow)',
    paddingLeft: 'var(--tc-card-padding-x)',
    paddingRight: 'var(--tc-card-padding-x)',
    paddingTop: 'var(--tc-card-padding-y)',
    paddingBottom: 'var(--tc-card-padding-y)'
  }),
  outline: () => ({
    border: 'var(--border-neutral-emphasis)',
    borderRadius: 'var(--tc-card-border-radius)',
    backgroundColor: 'transparent',
    boxShadow: 'var(--tc-card-shadow)',
    paddingLeft: 'var(--tc-card-padding-x)',
    paddingRight: 'var(--tc-card-padding-x)',
    paddingTop: 'var(--tc-card-padding-y)',
    paddingBottom: 'var(--tc-card-padding-y)'
  }),
  frosted: () => ({
    position: 'relative',
    border: 'var(--tc-card-border)',
    borderRadius: 'var(--tc-card-border-radius)',
    backgroundColor: 'color-mix(in srgb, var(--tc-card-bg-color) 50%, transparent)',
    boxShadow: 'var(--shadow-md)',
    paddingLeft: 'var(--tc-card-padding-x)',
    paddingRight: 'var(--tc-card-padding-x)',
    paddingTop: 'var(--tc-card-padding-y)',
    paddingBottom: 'var(--tc-card-padding-y)',
    '&::before': {
      backdropFilter: 'blur(10px)',
      content: '""',
      position: 'absolute',
      display: 'block',
      width: '100%',
      height: '100%',
      top: 0,
      left: 0,
      zIndex: -1
    }
  }),
  clear: () => ({
    paddingLeft: 'var(--tc-card-padding-x)',
    paddingRight: 'var(--tc-card-padding-x)',
    paddingTop: 'var(--tc-card-padding-y)',
    paddingBottom: 'var(--tc-card-padding-y)'
  }),
  primary: (theme) => ({
    border: 'var(--border-primary-normal)',
    borderWidth: '2px',
    borderStyle: 'solid',
    borderColor: 'color-mix(in srgb, var(--color-primary-500) 75%, #ffffff)',
    borderRadius: 'var(--border-radius-md)',
    backgroundColor: 'var(--color-primary-500)',
    color: getContrastRatio(cssVar('--text-color-main'), '#fff') > 4.5 ? '#fff' : cssVar('--text-color-main'),
    boxShadow: theme.layout.shadow.medium,
    padding: theme.layout.padding.thick
  }),
  secondary: (theme) => ({
    borderWidth: '2px',
    borderStyle: 'solid',
    borderColor: 'color-mix(in srgb, var(--color-secondary-500) 75%, #ffffff)',
    borderRadius: 'var(--border-radius-md)',
    backgroundColor: 'var(--color-secondary-500)',
    color: getContrastRatio(cssVar('--text-color-main'), '#fff') > 4.5 ? '#fff' : cssVar('--text-color-main'),
    boxShadow: theme.layout.shadow.medium,
    padding: theme.layout.padding.thick
  }),
  accent: (theme) => ({
    borderWidth: '2px',
    borderStyle: 'solid',
    borderColor: 'color-mix(in srgb, var(--color-accent-500) 75%, #ffffff)',
    borderRadius: 'var(--border-radius-md)',
    backgroundColor: 'var(--color-accent-500)',
    color: getContrastRatio(cssVar('--text-color-main'), '#fff') > 4.5 ? '#fff' : cssVar('--text-color-main'),
    boxShadow: theme.layout.shadow.medium,
    padding: theme.layout.padding.thick
  }),
  primaryText: (theme) => ({
    borderWidth: '2px',
    borderStyle: 'solid',
    borderColor: 'var(--color-primary-500)',
    borderRadius: 'var(--border-radius-md)',
    color: 'var(--color-primary-500)',
    boxShadow: theme.layout.shadow.medium,
    padding: theme.layout.padding.thick
  }),
  secondaryText: (theme) => ({
    borderWidth: '2px',
    borderStyle: 'solid',
    borderColor: 'var(--color-accent-500)',
    borderRadius: 'var(--border-radius-md)',
    color: 'var(--color-secondary-500)',
    boxShadow: theme.layout.shadow.medium,
    padding: theme.layout.padding.thick
  }),
  accentText: (theme) => ({
    borderWidth: '2px',
    borderStyle: 'solid',
    borderColor: 'var(--color-accent-500)',
    borderRadius: 'var(--border-radius-md)',
    color: 'var(--color-accent-500)',
    boxShadow: theme.layout.shadow.medium,
    padding: theme.layout.padding.thick
  })
}

const mapVariant = (theme, variant) => {
  if (variant in cardVariants) {
    return cardVariants[variant](theme)
  }

  return cardVariants.default()
}

const useStyles = makeStyles((theme) => ({
  card: ({ variant }) => ({
    ...(mapVariant(theme, variant))
  })
}))

function Card ({ variant, children, className, style, ...rest }) {
  const classes = useStyles({ variant })

  return (
    <div className={clsx(classes.card, className)} style={style} {...rest}>
      {children}
    </div>
  )
}

Card.propTypes = {
  variant: PropTypes.oneOf(['default', 'outline', 'frosted', 'clear', 'primary', 'secondary', 'accent', 'primaryText', 'secondaryText', 'accentText']),
  children: PropTypes.node,
  className: PropTypes.string,
  style: PropTypes.object
}

Card.defaultProps = {
  variant: 'default'
}

export default Card
